import { PAYORS } from "./registry.mjs";

//#region src/payors/tenantPayors.ts
/**
* Plan-type strings as Availity spells them, spacing variants included. Same for
* every tenant. Leaves `referralsRequired` unset, routing the call to a human.
*/
const INSURANCE_TYPE_MAPPING = {
	"Health Maintenance Organization(HMO)": { planType: "HMO" },
	"Health Maintenance Organization (HMO)": { planType: "HMO" },
	"Preferred Provider Organization(PPO)": { planType: "PPO" },
	"Preferred Provider Organization (PPO)": { planType: "PPO" },
	"Exclusive Provider Organization (EPO)": { planType: "EPO" },
	"Point of Service (POS)": { planType: "POS" },
	"Health Savings Account (HSA)": { planType: "HSA" },
	"High Deductible Health Plan (HDHP)": { planType: "HDHP" }
};
/** Canonical payor name → the text to type into Availity's payer field. */
function availityPayorNameMapping(registry) {
	const mapping = {};
	for (const entry of registry) if (entry.availityName) mapping[entry.payor] = entry.availityName;
	return mapping;
}
/** The portal handling a payor, or undefined when none does. */
function portalFor(entry) {
	return entry.portal ?? (entry.availityName ? "availity" : void 0);
}
/** Canonical payor name → the portal that handles it. */
function payorPortalMapping(registry) {
	const mapping = {};
	for (const entry of registry) {
		const portal = portalFor(entry);
		if (portal) mapping[entry.payor] = portal;
	}
	return mapping;
}
/**
* The tenant's `availity` clinic config. Serialized into hosted Libretto job
* params and read out of process, so the shape stays plain `Record<string,
* string>` rather than keyed on `CanonicalPayor`.
*/
function buildAvailityConfig(registry, options) {
	return {
		payorNameToAvailityPayorNameMapping: availityPayorNameMapping(registry),
		payorToPortalMapping: payorPortalMapping(registry),
		insuranceTypeMapping: INSURANCE_TYPE_MAPPING,
		insuranceGroupToInfoMapping: options.insuranceGroupToInfoMapping
	};
}
/** Payor select options, sorted by what staff read, not the code we store. */
function payorDropdownOptions(registry) {
	return registry.map((entry) => ({
		value: entry.payor,
		label: PAYORS[entry.payor].label
	})).sort((a, b) => a.label.localeCompare(b.label));
}
/**
* Any payor with a portal. Not the same as having an Availity name: HHB reaches
* UHC through UHC's own workflow, which `availityName` would wrongly exclude.
*/
function eligibilityPayors(registry) {
	return registry.filter((e) => portalFor(e)).map((e) => e.payor);
}
/** Payors this tenant can file a prior auth for. */
function priorAuthPayors(registry) {
	return registry.filter((e) => e.priorAuth).map((e) => e.payor);
}
/** Membership test for an already-normalized payor, which is typed as string. */
function supportsPayor(payors, payor) {
	return payors.some((supported) => supported === payor);
}

//#endregion
export { INSURANCE_TYPE_MAPPING, availityPayorNameMapping, buildAvailityConfig, eligibilityPayors, payorDropdownOptions, payorPortalMapping, priorAuthPayors, supportsPayor };